import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import Panzoom, { type PanzoomObject } from '@panzoom/panzoom';

import { bindZoomGestures, MAX_SCALE, MIN_SCALE, ZOOMED_ABOVE } from './stageZoomGestures';

export interface StageZoom {
    isZoomed: boolean;
    canZoomIn: boolean;
    zoomIn: () => void;
    zoomOut: () => void;
    reset: () => void;
}

interface UseStageZoomArgs {
    /** The element Panzoom transforms: the <figure> holding the image and its boxes. */
    figureRef: RefObject<HTMLElement | null>;
    /**
     * The image's path, or null while there is nothing to zoom. A new path gets a fresh
     * instance, so zoom and pan never carry over to the next image.
     */
    imageKey: string | null;
    /** The magnifier reads pointer offsets against the unzoomed image, so zoom is off with it. */
    disabled: boolean;
    onZoomedChange?: (zoomed: boolean) => void;
}

function prefersReducedMotion(): boolean {
    return typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
}

/**
 * Wheel, pinch, double-click or double-tap and the zoom buttons, on one Panzoom instance per
 * image. The figure is not remounted between images, so the previous transform is cleared
 * before each instance is built.
 *
 * Unzoomed, the image leaves the pointer alone: Panzoom's default start handler cancels the
 * pointerdown, which would also cancel the mouse events the swipe-to-next listens for, and
 * `touch-action: pan-y` lets the page scroll vertically over the image on a phone. Zoomed,
 * Panzoom takes the pointer to pan and the page stops scrolling under the finger.
 */
export function useStageZoom(args: UseStageZoomArgs): StageZoom {
    const { figureRef, imageKey, disabled, onZoomedChange } = args;

    const panzoomRef = useRef<PanzoomObject | null>(null);
    const [scale, setScale] = useState(MIN_SCALE);
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;
    const onZoomedChangeRef = useRef(onZoomedChange);
    onZoomedChangeRef.current = onZoomedChange;

    const isZoomed = scale > ZOOMED_ABOVE;

    useEffect(() => {
        onZoomedChangeRef.current?.(isZoomed);
    }, [isZoomed]);

    useEffect(() => {
        // Every image starts unzoomed, including one that cannot be shown at all.
        setScale(MIN_SCALE);
        const node = figureRef.current;
        const stage = node?.parentElement;
        if (!node || !stage || imageKey === null) {
            return;
        }

        node.style.transform = '';
        // Read by Box.module.scss to keep the detection labels their size while zoomed.
        node.style.setProperty('--zoom-scale', String(MIN_SCALE));
        let currentScale = MIN_SCALE;

        const panzoom: PanzoomObject = Panzoom(node, {
            maxScale: MAX_SCALE,
            minScale: MIN_SCALE,
            startScale: MIN_SCALE,
            startX: 0,
            startY: 0,
            animate: !prefersReducedMotion(),
            easing: 'ease-in-out',
            step: 0.3,
            panOnlyWhenZoomed: true,
            contain: 'outside',
            cursor: '',
            touchAction: 'pan-y',
            handleStartEvent: (event: Event) => {
                if (currentScale > ZOOMED_ABOVE) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            },
        });
        panzoomRef.current = panzoom;

        let recentreTimeout: ReturnType<typeof setTimeout> | undefined;
        const onChange = (event: Event) => {
            if (!(event instanceof CustomEvent)) {
                return;
            }
            const next: unknown = event.detail?.scale;
            if (typeof next !== 'number') {
                return;
            }
            const wasZoomed = currentScale > ZOOMED_ABOVE;
            currentScale = next;
            setScale(next);
            node.style.setProperty('--zoom-scale', String(next));
            const zoomed = next > ZOOMED_ABOVE;
            if (zoomed !== wasZoomed) {
                panzoom.setOptions({
                    touchAction: zoomed ? 'none' : 'pan-y',
                    cursor: zoomed ? 'move' : '',
                });
            }
            // Back at (nearly) full size, centre the image again rather than leave it offset.
            if (next < 1.1) {
                recentreTimeout = setTimeout(() => panzoom.pan(0, 0));
            }
        };

        const unbindGestures = bindZoomGestures({
            node,
            stage,
            panzoom,
            scale: () => currentScale,
            isDisabled: () => disabledRef.current,
        });

        node.addEventListener('panzoomchange', onChange);

        return () => {
            clearTimeout(recentreTimeout);
            node.removeEventListener('panzoomchange', onChange);
            unbindGestures();
            panzoom.destroy();
            panzoomRef.current = null;
        };
    }, [figureRef, imageKey]);

    // Turning the magnifier on mid-zoom would magnify the wrong spot, so it starts unzoomed.
    useEffect(() => {
        if (disabled) {
            panzoomRef.current?.reset({ animate: false });
        }
    }, [disabled]);

    const zoomIn = useCallback(() => {
        if (!disabledRef.current) {
            panzoomRef.current?.zoomIn();
        }
    }, []);
    const zoomOut = useCallback(() => {
        if (!disabledRef.current) {
            panzoomRef.current?.zoomOut();
        }
    }, []);
    const reset = useCallback(() => {
        panzoomRef.current?.reset();
    }, []);

    return { isZoomed, canZoomIn: scale < MAX_SCALE - 0.01, zoomIn, zoomOut, reset };
}
